import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SaveChip } from "@/components/ui/SaveChip";
import { useAutosaveIndicator } from "@/hooks/useAutosaveIndicator";
import { ExternalLink, Link2, Plus, Trash2, FileText, ChevronDown, ChevronRight } from "lucide-react";

const sb: any = supabase as any;

interface Props {
  assessmentId: string;
  findingId: string;
  disabled?: boolean;
}

interface EvidenceRow {
  evidence_id: string;
  evidence_type: string | null;
  link_url: string | null;
  file_url: string | null;
  title: string | null;
  description: string | null;
  source: string | null;
  added_by_role: string | null;
  uploaded_by_role: string | null;
  question_id: number | null;
  linked_at: string | null;
  note: string | null;
  link_kind: "explicit" | "inferred_by_question";
}

interface Justification {
  justification: string;
  observed_gap: string;
  recommended_additional_evidence: string;
  legacy_evidence_reviewed: string;
}

const EMPTY: Justification = {
  justification: "",
  observed_gap: "",
  recommended_additional_evidence: "",
  legacy_evidence_reviewed: "",
};

export const FindingJustificationEvidence: React.FC<Props> = ({
  assessmentId,
  findingId,
  disabled = false,
}) => {
  const [just, setJust] = useState<Justification>(EMPTY);
  const [evidence, setEvidence] = useState<EvidenceRow[]>([]);
  const [expectedEvidence, setExpectedEvidence] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showLegacy, setShowLegacy] = useState(false);
  const [form, setForm] = useState({ url: "", title: "", description: "", source: "" });
  const [saving, setSaving] = useState(false);
  const { state, begin, succeed, fail } = useAutosaveIndicator(200, 1500);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [jRes, eRes, expRes] = await Promise.all([
        sb.rpc("get_finding_justification", { p_finding_id: findingId }),
        sb.rpc("get_finding_evidence", { p_finding_id: findingId }),
        sb.rpc("get_finding_expected_evidence", { p_finding_id: findingId }),
      ]);
      const j = Array.isArray(jRes?.data) ? jRes.data[0] : jRes?.data;
      setJust({
        justification: j?.justification ?? "",
        observed_gap: j?.observed_gap ?? "",
        recommended_additional_evidence: j?.recommended_additional_evidence ?? "",
        legacy_evidence_reviewed: j?.evidence_reviewed ?? "",
      });
      setEvidence(Array.isArray(eRes?.data) ? eRes.data : []);
      setExpectedEvidence(Array.isArray(expRes?.data) ? expRes.data : []);
    } catch (e) {
      console.error("[FindingJustificationEvidence] load failed", e);
    } finally {
      setLoading(false);
    }
  }, [findingId]);

  useEffect(() => {
    load();
  }, [load]);

  const persistJustification = useCallback(
    async (patch: Partial<Justification>) => {
      const next = { ...just, ...patch };
      setJust(next);
      begin();
      try {
        const { error } = await sb.rpc("reviewer_set_finding_justification", {
          p_finding_id: findingId,
          p_justification: next.justification || null,
          p_observed_gap: next.observed_gap || null,
          p_recommended_additional_evidence: next.recommended_additional_evidence || null,
        });
        if (error) throw error;
        succeed();
      } catch (e) {
        console.error("[FindingJustificationEvidence] save justification failed", e);
        fail();
      }
    },
    [just, findingId, begin, succeed, fail]
  );

  const formHasAnyField =
    !!form.url.trim() ||
    !!form.title.trim() ||
    !!form.source.trim() ||
    !!form.description.trim();

  const handleAddLink = async () => {
    if (!formHasAnyField) return;
    setSaving(true);
    try {
      const { data: newId, error } = await sb.rpc("reviewer_add_evidence_link", {
        p_assessment_id: assessmentId,
        p_url: form.url.trim() || null,
        p_title: form.title.trim() || null,
        p_description: form.description.trim() || null,
        p_source: form.source.trim() || null,
        p_question_id: null,
      });
      if (error) throw error;
      const { error: linkErr } = await sb.rpc("reviewer_link_evidence_to_finding", {
        p_finding_id: findingId,
        p_evidence_id: newId,
        p_note: null,
      });
      if (linkErr) throw linkErr;
      setForm({ url: "", title: "", description: "", source: "" });
      setShowAdd(false);
      await load();
    } catch (e) {
      console.error("[FindingJustificationEvidence] add submitted evidence failed", e);
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async (evId: string) => {
    try {
      const { error } = await sb.rpc("reviewer_unlink_evidence_from_finding", {
        p_finding_id: findingId,
        p_evidence_id: evId,
      });
      if (error) throw error;
      await load();
    } catch (e) {
      console.error("[FindingJustificationEvidence] unlink failed", e);
    }
  };

  // Auto-generated Evidence Reviewed summary (derived, never written)
  const autoEvidenceReviewed = useMemo(() => {
    if (evidence.length === 0) return "";
    return evidence
      .map((e) => {
        const label = e.title || e.link_url || e.file_url || "(untitled)";
        const src = e.source ? ` — ${e.source}` : "";
        const auto = e.link_kind === "inferred_by_question" ? " [auto]" : "";
        return `• ${label}${src}${auto}`;
      })
      .join("\n");
  }, [evidence]);

  return (
    <div className="rounded-md border border-dashed border-border bg-muted/20 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground uppercase">
          Finding Justification &amp; Supporting Evidence
        </span>
        <SaveChip state={state} />
      </div>

      {loading ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : (
        <>
          <JustField
            label="Justification / Rationale"
            value={just.justification}
            disabled={disabled}
            onSave={(v) => persistJustification({ justification: v })}
            helper={`Example: "This finding was created because the submitted evidence did not demonstrate that the control is fully implemented. The reviewer observed that policies or procedures may exist, but operational evidence was insufficient to confirm consistent execution."`}
          />

          <JustField
            label="Observed Gap"
            value={just.observed_gap}
            disabled={disabled}
            placeholder="e.g. No security KPI dashboard or recurring metrics evidence was provided."
            onSave={(v) => persistJustification({ observed_gap: v })}
            helper="Example: No security KPI dashboard or recurring metrics evidence was provided to demonstrate ongoing monitoring."
          />

          {/* Evidence Reviewed — auto-generated, read-only */}
          <div>
            <label className="text-[11px] font-medium text-muted-foreground uppercase mb-1 block">
              Evidence Reviewed (Auto-generated)
            </label>
            <div className="rounded-md border border-border bg-background/60 px-3 py-2 text-sm whitespace-pre-line min-h-[40px] text-muted-foreground">
              {autoEvidenceReviewed || (
                <span className="italic">No evidence linked yet.</span>
              )}
            </div>
          </div>

          {/* Legacy evidence_reviewed — collapsible, read-only */}
          {just.legacy_evidence_reviewed.trim() && (
            <div>
              <button
                type="button"
                onClick={() => setShowLegacy((s) => !s)}
                className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground uppercase hover:text-foreground"
              >
                {showLegacy ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
                Legacy Evidence Reviewed Notes
              </button>
              {showLegacy && (
                <div className="mt-1 rounded-md border border-border bg-background/60 px-3 py-2 text-sm whitespace-pre-line text-muted-foreground italic">
                  {just.legacy_evidence_reviewed}
                </div>
              )}
            </div>
          )}

          <JustField
            label="Recommended Additional Evidence"
            value={just.recommended_additional_evidence}
            disabled={disabled}
            onSave={(v) => persistJustification({ recommended_additional_evidence: v })}
            helper={`Example: "Provide screenshots, reports, logs, dashboards, policies, procedures, system exports, meeting records, or validation evidence that demonstrate the control is operating effectively."`}
          />

          {/* Evidence-to-Review pills from the associated question */}
          <div>
            <label className="text-[11px] font-medium text-muted-foreground uppercase mb-1 block">
              Evidence to Review for Associated Question(s)
            </label>
            {expectedEvidence.length === 0 ? (
              <div className="text-xs text-muted-foreground italic">
                No expected evidence list defined on the associated question.
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {expectedEvidence.map((t, i) => (
                  <Badge
                    key={`${t}-${i}`}
                    variant="outline"
                    className="text-[11px] px-2 py-0.5 font-normal"
                  >
                    {t}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Linked Evidence */}
          <div className="space-y-2 pt-2 border-t border-border">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-medium text-muted-foreground uppercase">
                Linked Evidence ({evidence.length})
              </span>
              {!disabled && !showAdd && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setShowAdd(true)}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Add Submitted Evidence
                </Button>
              )}
            </div>

            {evidence.length === 0 && !showAdd && (
              <div className="text-xs text-muted-foreground italic">
                No evidence linked to this finding.
              </div>
            )}

            {evidence.map((e) => (
              <EvidenceRowDisplay
                key={e.evidence_id}
                row={e}
                disabled={disabled}
                onUnlink={() => handleUnlink(e.evidence_id)}
              />
            ))}

            {showAdd && (
              <div className="rounded border border-border bg-background p-3 space-y-2">
                <div className="text-xs font-semibold text-foreground">
                  Add Submitted Evidence
                </div>
                <p className="text-[11px] text-muted-foreground -mt-1">
                  Title is strongly recommended — it is the only field shown in the
                  generated report's Evidence Reviewed section.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input
                    placeholder="Secure link URL"
                    value={form.url}
                    onChange={(e) => setForm((p) => ({ ...p, url: e.target.value }))}
                  />
                  <Input
                    placeholder="Title (recommended)"
                    value={form.title}
                    onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  />
                  <Input
                    placeholder="Source (e.g. Vendor SOC 2)"
                    value={form.source}
                    onChange={(e) => setForm((p) => ({ ...p, source: e.target.value }))}
                  />
                  <Input
                    placeholder="Description"
                    value={form.description}
                    onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  />
                </div>
                <div className="flex items-center justify-end gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowAdd(false);
                      setForm({ url: "", title: "", description: "", source: "" });
                    }}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleAddLink}
                    disabled={saving || !formHasAnyField}
                  >
                    {saving ? "Adding…" : "Add evidence"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const JustField: React.FC<{
  label: string;
  value: string;
  disabled: boolean;
  placeholder?: string;
  helper?: string;
  onSave: (v: string) => void;
}> = ({ label, value, disabled, placeholder, helper, onSave }) => {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  return (
    <div>
      <label className="text-[11px] font-medium text-muted-foreground uppercase mb-1 block">
        {label}
      </label>
      <Textarea
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => {
          if (local !== value) onSave(local);
        }}
        disabled={disabled}
        className="min-h-[60px] text-sm"
        placeholder={placeholder ?? "—"}
      />
      {helper && (
        <p className="mt-1 text-[11px] italic text-muted-foreground leading-snug">
          {helper}
        </p>
      )}
    </div>
  );
};

const EvidenceRowDisplay: React.FC<{
  row: EvidenceRow;
  disabled: boolean;
  onUnlink: () => void;
}> = ({ row, disabled, onUnlink }) => {
  const isLink = row.evidence_type === "link" && !!row.link_url;
  const role = row.added_by_role || row.uploaded_by_role || "—";
  const isInferred = row.link_kind === "inferred_by_question";

  return (
    <div className="flex items-start gap-2 rounded border border-border bg-background p-2.5">
      <div className="mt-0.5 text-muted-foreground">
        {isLink ? <Link2 className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
      </div>
      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          {isLink ? (
            <a
              href={row.link_url!}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-primary hover:underline truncate inline-flex items-center gap-1"
            >
              {row.title || row.link_url}
              <ExternalLink className="h-3 w-3 shrink-0" />
            </a>
          ) : (
            <span className="text-sm font-medium truncate">
              {row.title || row.file_url || "Evidence"}
            </span>
          )}
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
            {role}
          </Badge>
          {isInferred && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
              auto (same question)
            </Badge>
          )}
        </div>
        {row.source && (
          <div className="text-xs text-muted-foreground">Source: {row.source}</div>
        )}
        {row.description && (
          <div className="text-xs text-muted-foreground">{row.description}</div>
        )}
      </div>
      {!disabled && !isInferred && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
          onClick={onUnlink}
          title="Unlink from finding"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
};
